'use strict';

import {
    TRIGGER_SHOW_DOWNLOAD_OPTIONS_CARD_EVENT,
    TRIGGER_UPDATE_ZOOM_ACTIVES_EVENT,
    UPDATE_LANGUAGE_EVENT,
} from '@core/constants/EVENTS';
import { DEFAULT_LANGUAGE, MIN_ACTIVES_HEIGHT } from '@core/constants/DEFAULT';
import { isVideoItem } from '@core/utils/active';
import { addDownloadOptionsList } from '@core/utils/download';
import { ripple } from '@core/utils/ripple';
import { WESOG_STORE_PARAMS } from '@core/constants/STORE';
import { getStoreValue } from '@core/utils/store';
import { ROUTES } from '@core/constants/ROUTES';
import { customDebounce } from '@core/utils/utils';
import {
    globalSharedStyles,
    typographySharedStyles,
    commonCardStyles,
    imageIconsStyles,
    customDownloadOptionsStyles,
    commonCustomModalStyles,
    customMenuDownStyleSheet,
    customCommonStyles,
} from '@core/styles';

import { calculateZoomValue } from '../../utils';
import { REPORT_CARD_ATTRIBUTES, REPORT_CARD_COMPONENT } from './CONSTANTS';
import {
    handleTranslationHelper,
    updateFormatCardDate,
    setupContainerStyles,
    setupImageElement,
    setupDescriptionElements,
    setupPhotosCount,
    configureLightboxMode,
    configureNormalMode,
    setupReportType,
    setupVideoConfiguration,
    addExclusiveWatermark,
    setupZoomConfiguration,
    getVideoUrl,
    showLoadingSpinner,
    hideLoadingSpinner,
} from './utils';

import htmlContent from './ReportCardComponent.html';
import cssContent from './ReportCardComponent.css';

/**
 * ReportCardComponent
 *
 * A web component that displays report cards with interactive features
 * including zoom, cart, lightbox, download, and video playback capabilities.
 *
 * @class ReportCardComponent
 * @extends {HTMLElement}
 */
class ReportCardComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            typographySharedStyles,
            commonCardStyles,
            imageIconsStyles,
            customDownloadOptionsStyles,
            commonCustomModalStyles,
            customMenuDownStyleSheet,
            customCommonStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        /** @type {Object} */
        this._img = null;
        /** @type {number} */
        this._minImgHeight = null;
        /** @type {boolean} */
        this._isVideoLoaded = false;
        /** @type {boolean} */
        this._isAddingElementToLightBox = false;
        /** @type {boolean} */
        this._isLoading = false;
        /** @type {any|undefined} */
        this._cachedVideoUrl = undefined;
        /** @type {boolean} */
        this._shouldPlayVideo = false;

        // Cache DOM references
        /** @type {HTMLDivElement|null} */
        this._container = null;
        /** @type {HTMLImageElement|null} */
        this._imgElement = null;
        /** @type {HTMLVideoElement|null} */
        this._videoElement = null;
        /** @type {HTMLSourceElement|null} */
        this._videoSource = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleUpdateZoomActivesBound = this._handleUpdateZoomActives.bind(this);
        this._handleShowDownloadOptionsBound = this._handleShowDownloadOptions.bind(this);
        this._handleMouseEnterBound = this._handleMouseEnter.bind(this);
        this._handleMouseLeaveBound = this._handleMouseLeave.bind(this);

        // Debounced handlers
        this._debouncedMouseEnter = customDebounce(this._handleMouseEnterBound, 100);
        this._debouncedMouseLeave = customDebounce(this._handleMouseLeaveBound, 100);
    }

    /**
     * Called when the element is connected to the DOM.
     * @returns {void}
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
        this._addRippleBehavior();
        this._setupInitialData();
        this.render();
    }

    /**
     * Called when the element is disconnected from the DOM.
     * @returns {void}
     */
    disconnectedCallback() {
        this._removeEventListeners();
        this._clearTimeouts();
    }

    /**
     * Cache DOM references for better performance.
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._container = this.shadowRoot.querySelector('[data-main-card-container]');
        this._imgElement = this.shadowRoot.querySelector('img');
        this._videoElement = this.shadowRoot.querySelector('video');
        this._videoSource = this.shadowRoot.querySelector('#video-source');
    }

    /**
     * Add event listeners to the component.
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.addEventListener(
            TRIGGER_UPDATE_ZOOM_ACTIVES_EVENT,
            this._handleUpdateZoomActivesBound,
        );
        document.addEventListener(
            TRIGGER_SHOW_DOWNLOAD_OPTIONS_CARD_EVENT,
            this._handleShowDownloadOptionsBound,
        );
    }

    /**
     * Remove event listeners from the component.
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.removeEventListener(
            TRIGGER_UPDATE_ZOOM_ACTIVES_EVENT,
            this._handleUpdateZoomActivesBound,
        );
        document.removeEventListener(
            TRIGGER_SHOW_DOWNLOAD_OPTIONS_CARD_EVENT,
            this._handleShowDownloadOptionsBound,
        );

        if (this._container) {
            this._container.removeEventListener('mouseenter', this._debouncedMouseEnter);
            this._container.removeEventListener('mouseleave', this._debouncedMouseLeave);
        }
    }

    /**
     * Clear any active timeouts to prevent memory leaks.
     * @returns {void}
     * @private
     */
    _clearTimeouts() {
        if (this._mouseEnterTimeout) {
            clearTimeout(this._mouseEnterTimeout);
            this._mouseEnterTimeout = null;
        }
        if (this._mouseLeaveTimeout) {
            clearTimeout(this._mouseLeaveTimeout);
            this._mouseLeaveTimeout = null;
        }
    }

    /**
     * Setup initial data from attributes.
     * @returns {void}
     * @private
     */
    _setupInitialData() {
        this._minImgHeight =
            this.getAttribute(REPORT_CARD_ATTRIBUTES.MIN_IMG_HEIGHT) ||
            MIN_ACTIVES_HEIGHT * calculateZoomValue();
        this._img = JSON.parse(this.getAttribute(REPORT_CARD_ATTRIBUTES.IMAGE));
    }

    /**
     * Render the component.
     * @param {boolean} [firstTime=true] - Whether this is the first render.
     * @returns {void}
     */
    render(firstTime = true) {
        if (!this._img || !this._container) {
            console.warn('ReportCardComponent: No image data or container provided');
            return;
        }

        hideLoadingSpinner(this.shadowRoot);

        const reportTitle = this.getAttribute(REPORT_CARD_ATTRIBUTES.REPORT_TITLE);
        const reportUid = this.getAttribute(REPORT_CARD_ATTRIBUTES.ID);
        const numberPhotos =
            parseInt(this.getAttribute(REPORT_CARD_ATTRIBUTES.PHOTOS)) || 0;
        const onePhotoOnly = numberPhotos === 1;

        // Setup container styles
        const { customFlexGrow } = setupContainerStyles(
            this._container,
            this._img,
            this._minImgHeight,
        );

        // Setup image element
        setupImageElement(this._imgElement, this._img, reportTitle);

        // Update date format
        updateFormatCardDate(
            this.shadowRoot,
            this.getAttribute(REPORT_CARD_ATTRIBUTES.DATE),
        );

        // Setup description elements
        setupDescriptionElements(this.shadowRoot, reportTitle, this._img, reportUid);

        // Setup photos count
        setupPhotosCount(this.shadowRoot, numberPhotos);

        // Calculate tooltip content width
        const tooltipContentWidth =
            this._img.img_width === 'auto' ? 150 : Math.min(customFlexGrow * 0.8, 150);

        // Configure based on lightbox mode
        const isLightboxMode =
            this.getAttribute(REPORT_CARD_ATTRIBUTES.LIGHTBOX) === 'true';
        if (isLightboxMode) {
            configureLightboxMode(
                this.shadowRoot,
                this._img,
                reportUid,
                customFlexGrow,
                tooltipContentWidth,
                onePhotoOnly,
            );
        } else {
            const hasPdf = this.getAttribute(REPORT_CARD_ATTRIBUTES.PDF);
            configureNormalMode(
                this.shadowRoot,
                this._img,
                reportUid,
                customFlexGrow,
                tooltipContentWidth,
                hasPdf,
            );
        }

        // Setup report type
        const currentReportType = this.getAttribute(
            REPORT_CARD_ATTRIBUTES.CURRENT_REPORT_TYPE,
        );
        const typeReport = this.getAttribute(REPORT_CARD_ATTRIBUTES.TYPE_REPORT);
        setupReportType(this.shadowRoot, currentReportType, typeReport);

        // Setup video configuration
        setupVideoConfiguration(
            this.shadowRoot,
            this._img,
            reportUid,
            customFlexGrow,
            tooltipContentWidth,
            onePhotoOnly,
        );

        // Add exclusive watermark if needed
        addExclusiveWatermark(this._container, this._imgElement, this._img);

        // Setup zoom configuration
        setupZoomConfiguration(this.shadowRoot);

        if (!firstTime) return;

        // Setup video event listeners for first time
        if (isVideoItem(this._img.img_zoom)) {
            this._setupVideoEventListeners();
        }
    }

    /**
     * Setup video event listeners.
     * @returns {void}
     * @private
     */
    _setupVideoEventListeners() {
        if (!this._container || !isVideoItem(this._img.img_zoom)) return;

        this._container.addEventListener('mouseenter', this._debouncedMouseEnter, {
            passive: true,
        });
        this._container.addEventListener('mouseleave', this._debouncedMouseLeave, {
            passive: true,
        });
    }

    /**
     * Handle mouse enter event for video playback.
     * @param {Event} event - The mouse enter event.
     * @returns {void}
     * @private
     */
    async _handleMouseEnter(event) {
        if (
            !this._img ||
            !isVideoItem(this._img.img_zoom) ||
            !this._videoElement ||
            !this._imgElement ||
            !this._videoSource
        )
            return;

        event.preventDefault();
        event.stopPropagation();

        showLoadingSpinner(this.shadowRoot);
        this._shouldPlayVideo = true;

        try {
            const videoUrl = await getVideoUrl(this._img.img_zoom);
            this._videoSource.setAttribute('src', videoUrl);
            this._videoElement.load();
            await this._videoElement.play();

            if (!this._shouldPlayVideo) {
                this._videoElement.pause();
            }

            this._isVideoLoaded = true;
            this._imgElement.style.display = 'none';
            this._videoElement.style.display = 'block';
        } catch (error) {
            console.error('Error loading video:', error);
        } finally {
            hideLoadingSpinner(this.shadowRoot);
        }
    }

    /**
     * Handle mouse leave event for video playback.
     * @param {Event} event - The mouse leave event.
     * @returns {void}
     * @private
     */
    _handleMouseLeave(event) {
        if (
            !this._img ||
            !isVideoItem(this._img.img_zoom) ||
            !this._videoElement ||
            !this._imgElement
        )
            return;

        event.preventDefault();
        event.stopPropagation();
        this._shouldPlayVideo = false;

        if (!this._container?.contains(event.relatedTarget)) {
            hideLoadingSpinner(this.shadowRoot);
            this._imgElement.style.display = 'block';
            this._videoElement.style.display = 'none';

            if (this._isVideoLoaded) {
                this._videoElement.pause();
            }
        }
    }

    /**
     * Handle translation helper.
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateFormatCardDate(
            this.shadowRoot,
            this.getAttribute(REPORT_CARD_ATTRIBUTES.DATE),
        );
        handleTranslationHelper(
            this.shadowRoot,
            getStoreValue(WESOG_STORE_PARAMS.CURRENT_LANGUAGE),
            DEFAULT_LANGUAGE,
        );
    }

    /**
     * Handle update zoom actives event.
     * @returns {void}
     * @private
     */
    _handleUpdateZoomActives() {
        if (window.location.pathname.includes(ROUTES.DETAILS)) return;

        this._minImgHeight = MIN_ACTIVES_HEIGHT * calculateZoomValue();
        if (this._img) {
            this._img.img_height = this._img.img_height || this._minImgHeight;
        }
        this.render(false);
    }

    /**
     * Handle show download options event.
     * @param {CustomEvent} event - The event.
     * @returns {void}
     * @private
     */
    _handleShowDownloadOptions(event) {
        if (!this._img || event.detail.elementId !== this._img.uid) return;

        addDownloadOptionsList(this, event.detail.options, this._img.reference);
        this._handleTranslationHelper();
    }

    /**
     * Add ripple behavior to elements with data-ripple attribute.
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        this.shadowRoot.querySelectorAll('[data-ripple]').forEach((element) => {
            ripple(element);
        });
    }
}

customElements.define(REPORT_CARD_COMPONENT, ReportCardComponent);
